package com.shadowings.gero.presentation.component

import com.shadowings.gero.presentation.util.DevicePreviews
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.ui.Alignment
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.NavigationBar
import androidx.compose.material3.NavigationBarItem
import androidx.compose.material3.NavigationRail
import androidx.compose.material3.NavigationRailItem
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.shadowings.gero.presentation.ui.AppTheme
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import gero.composeapp.generated.resources.*

import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.material3.Badge
import androidx.compose.material3.BadgedBox
import androidx.compose.material3.Icon
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.padding
import compose.icons.FeatherIcons
import compose.icons.feathericons.Lock

// Ordine sezioni: 0 Impara (Capitoli/Correggi come tab interne), 1 Compendio, 2 Builder,
// 3 Personaggi, 4 Profilo.
private fun isSectionLocked(index: Int, starCount: Int): Boolean {
    return when (index) {
        1 -> starCount < 1   // Compendio needs 1 star
        2 -> starCount < 30  // Builder needs 30 stars
        3 -> starCount < 10  // Personaggi needs 10 stars
        else -> false
    }
}

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun BottomBarPreview() {
    AppTheme {
        Surface {
            BottomBar(
                selectedIndex = 0, isLoggedIn = false, language = "it", starCount = 0,
                onItemSelected = {}
            )
        }
    }
}


@Composable
fun BottomBar(
    selectedIndex: Int,
    isLoggedIn: Boolean,
    language: String,
    starCount: Int = 0,
    onItemSelected: (Int) -> Unit
) {
    val hapticFeedback = LocalHapticFeedback.current
    // Use language as key to force recomposition and re-evaluation of stringResource when language changes
    key(language) {
        val useShort = LocalDensity.current.fontScale > 1.49f
        val iconSize = if (useShort) 34.dp else 42.dp
        val sections: List<Pair<String, org.jetbrains.compose.resources.DrawableResource>> = listOf(
            stringResource(if (useShort) Res.string.bar_title_chapters_short else Res.string.bar_title_chapters) to Res.drawable.A28,
            stringResource(if (useShort) Res.string.bar_title_compendio_short else Res.string.bar_title_compendio) to Res.drawable.A8,
            stringResource(if (useShort) Res.string.bar_title_builder_short else Res.string.bar_title_builder) to Res.drawable.A34,
            stringResource(if (useShort) Res.string.bar_title_personaggi_short else Res.string.bar_title_personaggi) to Res.drawable.A36,
            stringResource(if (useShort) Res.string.bar_title_profile_short else Res.string.bar_title_profile) to Res.drawable.A2,
        )

        NavigationBar(
            containerColor = MaterialTheme.colorScheme.background,
            modifier = Modifier.semantics { testTag = "bottom_bar" }
        ) {
            Column {
                HorizontalDivider()
                Row {
                    sections.forEachIndexed { index, pair ->
                        val isLocked = isSectionLocked(index, starCount)
                        NavigationBarItem(
                            modifier = Modifier.semantics {
                                testTag = "bottom_bar_item_${pair.first.lowercase()}"
                            },
                            selected = selectedIndex == index,
                            onClick = {
                                hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                                onItemSelected(index)
                            },
                            icon = {
                                val tintColor = if (isLocked) {
                                    MaterialTheme.colorScheme.onSurface.copy(alpha = 0.38f)
                                } else {
                                    MaterialTheme.colorScheme.onSurface
                                }
                                Box {
                                    // Pallino rosso sul Profilo per chi non ha ancora fatto login.
                                    if (index == 4 && !isLoggedIn && !isLocked) {
                                        BadgedBox(badge = { Badge() }) {
                                            Image(
                                                modifier = Modifier.size(iconSize),
                                                painter = painterResource(pair.second),
                                                contentDescription = pair.first,
                                                colorFilter = ColorFilter.tint(tintColor)
                                            )
                                        }
                                    } else {
                                        Image(
                                            modifier = Modifier.size(iconSize),
                                            painter = painterResource(pair.second),
                                            contentDescription = pair.first,
                                            colorFilter = ColorFilter.tint(tintColor)
                                        )
                                    }
                                    if (isLocked) {
                                        Icon(
                                            imageVector = FeatherIcons.Lock,
                                            contentDescription = stringResource(Res.string.locked_icon_desc),
                                            tint = MaterialTheme.colorScheme.error,
                                            modifier = Modifier
                                                .size(16.dp)
                                                .align(Alignment.BottomEnd)
                                                .background(
                                                    color = MaterialTheme.colorScheme.surface,
                                                    shape = androidx.compose.foundation.shape.CircleShape
                                                )
                                                .padding(2.dp)
                                        )
                                    }
                                }
                            },
                            label = {
                                val tintColor = if (isLocked) {
                                    MaterialTheme.colorScheme.onSurface.copy(alpha = 0.38f)
                                } else {
                                    MaterialTheme.colorScheme.onSurface
                                }
                                Text(
                                    text = pair.first,
                                    color = tintColor,
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis
                                )
                            }
                        )
                    }
                }
            }
        }
    }
}

